/**
 * The filter bar above the ticket list.
 *
 * A `Toolbar` rather than a row of loose controls: it gives arrow-key roving
 * focus across the filters, which matters on a page whose main content is a
 * grid the keyboard also walks through.
 *
 * Every control writes straight to the URL params hook, so there is no local
 * copy of the filter state to drift — except the search box, which keeps a
 * keystroke-local value and commits it on a short debounce so typing does not
 * fire a request per character.
 *
 * ## Why the widths are spelled out
 *
 * `Select.Trigger` ships `width: 100%` because its home is inside a `Field`,
 * where a form control should fill its column. Dropped into a flex row that
 * makes every select claim the full line, and the bar collapses into one
 * control per row — which is exactly how this filter bar used to render. Each
 * select is therefore pinned back to `w-auto` (a Tailwind utility, so it wins
 * over `@layer norican`) and the search box is the only thing allowed to grow.
 * Below `sm` the search takes its own full-width line and the three filters sit
 * under it, which is the right shape on a phone.
 */

import { Icon, Search, X } from "@norican/icons";
import { Button, FilterSelect, Input, Select, Toolbar } from "@norican/react";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";

import type { TicketBucket, TicketKind, TicketStatusCode } from "../api/contract";
import { TICKET_BUCKETS, TICKET_KINDS, TICKET_STATUS_CODES } from "../lib/ticket-display";
import type { TicketListParams } from "../lib/use-ticket-list-params";

/** Sentinel for "no type filter" — Base UI selects need a concrete value. */
const ANY_KIND = "ANY";

/** How long the search box waits after the last keystroke before querying. */
const SEARCH_DEBOUNCE_MS = 350;

interface SelectOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
}

/** One single-choice dropdown, so bucket and type stay visually identical. */
function FilterDropdown({ label, value, options, onValueChange }: FilterDropdownProps) {
  return (
    <Select.Root
      value={value}
      items={options}
      onValueChange={(next) => onValueChange(String(next))}
    >
      <Select.Trigger size="sm" aria-label={label} className="w-auto shrink-0 gap-2" />
      <Select.Portal>
        <Select.Positioner sideOffset={4}>
          <Select.Popup>
            <Select.List>
              {options.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

export interface TicketFiltersProps {
  params: TicketListParams;
  onChange: (patch: Partial<TicketListParams>) => void;
  onReset: () => void;
  isFiltered: boolean;
  /**
   * Hide the bucket control.
   *
   * On My Support each section *is* a bucket — Waiting on Me, open records,
   * recently resolved — so offering the selector there would let someone put
   * resolved tickets under "Waiting on Me" and be right to be confused by the
   * result. The section owns that axis; the filters own the rest.
   */
  hideBucket?: boolean;
}

export function TicketFilters({
  params,
  onChange,
  onReset,
  isFiltered,
  hideBucket = false,
}: TicketFiltersProps) {
  const { t } = useT("tickets");
  const [searchDraft, setSearchDraft] = useState(params.q);

  // The URL is the source of truth: a back-navigation or a "clear filters"
  // rewrites `params.q`, and the box has to follow it.
  useEffect(() => {
    setSearchDraft(params.q);
  }, [params.q]);

  useEffect(() => {
    if (searchDraft === params.q) {
      return undefined;
    }
    const timer = window.setTimeout(() => onChange({ q: searchDraft }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, params.q, onChange]);

  const bucketOptions: SelectOption[] = TICKET_BUCKETS.map((bucket) => ({
    value: bucket,
    label: t(`bucket.${bucket}`),
  }));

  const kindOptions: SelectOption[] = [
    { value: ANY_KIND, label: t("filter.anyKind") },
    ...TICKET_KINDS.map((kind) => ({ value: kind, label: t(`kind.${kind}`) })),
  ];

  const statusOptions = TICKET_STATUS_CODES.map((code) => ({
    value: code,
    label: t(`status.${code}`),
    searchText: t(`status.${code}`),
  }));

  return (
    <Toolbar.Root
      aria-label={t("filter.label")}
      className="flex w-full flex-wrap items-center gap-2 p-2"
    >
      <Input
        size="sm"
        type="search"
        value={searchDraft}
        onChange={(event) => setSearchDraft(event.target.value.slice(0, 200))}
        placeholder={t("filter.searchPlaceholder")}
        aria-label={t("filter.search")}
        startSlot={<Icon icon={Search} size="sm" />}
        className="min-w-0 grow basis-full sm:basis-50"
      />

      {hideBucket ? null : (
        <FilterDropdown
          label={t("filter.bucket")}
          value={params.bucket}
          options={bucketOptions}
          onValueChange={(value) => onChange({ bucket: value as TicketBucket })}
        />
      )}

      <FilterDropdown
        label={t("filter.kind")}
        value={params.kind ?? ANY_KIND}
        options={kindOptions}
        onValueChange={(value) =>
          onChange({ kind: value === ANY_KIND ? null : (value as TicketKind) })
        }
      />

      {/* DS-GAP: this control's "No matches" and "Clear" strings are
          English-only inside @norican/react — see docs/norican-ds-gaps.md. */}
      <FilterSelect
        size="sm"
        className="shrink-0"
        options={statusOptions}
        value={params.status}
        onValueChange={(value) => onChange({ status: value as TicketStatusCode[] })}
        placeholder={t("filter.status")}
        noun={t("filter.statusNoun")}
        searchPlaceholder={t("filter.searchStatus")}
        aria-label={t("filter.status")}
      />

      {isFiltered ? (
        <Toolbar.Button
          className="ms-auto shrink-0"
          render={
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              startIcon={<Icon icon={X} size="sm" />}
            />
          }
          onClick={onReset}
        >
          {t("filter.clear")}
        </Toolbar.Button>
      ) : null}
    </Toolbar.Root>
  );
}
